import { useState } from "react";
import PropTypes from "prop-types";
import BoxControls from "../../../Common/Box/BoxControls";
import Figure from "../../../Common/Box/Figure";
import useAreaQuery from "../../../../hooks/useAreaQuery";
import { capitalizePhrase, getTooltip } from "../../../../utilities";

const getSeriesColor = (name, theme) => {
  switch (name) {
    case "Purpose-made":
      return theme.palette.areas.menstrualHygiene.primary;

    case "Non-purpose-made":
      return theme.palette.areas.general.secondary;

    default:
      return "black";
  }
};

const options = (data, props) => (theme) => {
  const absolute = props.comparisonMode !== "relative";

  const series = data.map((series) => ({
    ...series,
    color: getSeriesColor(series.name, theme),
  }));

  return {
    chart: {
      type: "column",
      width: 600,
      height: 300,
      backgroundColor: null,
    },
    xAxis: {
      categories: [
        props.regionShortName,
        "Low",
        "Lower-Middle",
        "Upper-Middle",
      ],
      title: undefined,
      labels: {
        autoRotation: undefined,
        useHTML: true,
        style: {
          color: theme.figure.axis.primary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
      plotLines: [
        {
          color: theme.figure.gridLineColor,
          dashStyle: "solid",
          value: 0.5,
          width: 3,
          zIndex: 2,
        },
      ],
    },
    yAxis: {
      title: undefined,
      reversedStacks: false,
      labels: {
        format: absolute ? undefined : "{value}%",
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    plotOptions: {
      column: {
        stacking: absolute ? "normal" : "percent",
        pointWidth: 30,
        borderWidth: 0,
      },
    },
    legend: {
      align: "right",
      verticalAlign: "middle",
      layout: "vertical",
      reversed: true,
      itemStyle: {
        fontSize: theme.figure.legend.fontSize,
      },
    },
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: function () {
        return getTooltip("Menstruators", this.y, this.percentage);
      },
      style: {
        fontSize: "0.875rem",
        color: theme.palette.tooltip.primary,
      },
    },
    exporting: {
      chartOptions: {
        title: undefined,
        subtitle: undefined,
      },
      enabled: false,
    },
    series,
  };
};

const MhmCsF1 = (props) => {
  const [comparisonMode, setComparisonMode] = useState("relative");

  const { areaData: data } = useAreaQuery(
    `/custom/mhm/cs/figures/1/${props.regionId}`
  );

  const handleComparisonModeToggle = () => {
    setComparisonMode((comparisonMode) =>
      comparisonMode === "absolute" ? "relative" : "absolute"
    );
  };

  return (
    <Figure
      id="MHM_CS_f1"
      number="1"
      title="Use of Menstrual Hygiene Products"
      subtitle={`${capitalizePhrase(
        props.regionName
      )} and Comparison by Income Group, 2019`}
      controls={
        <BoxControls
          comparisionMode={comparisonMode}
          onToggleComparisonMode={handleComparisonModeToggle}
        />
      }
      chartOptions={
        data &&
        options(data, {
          regionName: props.regionName,
          regionShortName: props.regionShortName,
          comparisonMode,
        })
      }
    />
  );
};

MhmCsF1.propTypes = {
  regionId: PropTypes.number.isRequired,
  regionName: PropTypes.string.isRequired,
  regionShortName: PropTypes.string.isRequired,
};

export default MhmCsF1;
